import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import { servicesApi, metricsApi, initiativesApi } from '../services/trackerApi';
import { peopleApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { reactSelectStyles } from '../utils/reactSelectStyles';

export const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: services = [] } = useQuery({ queryKey: ['services'], queryFn: servicesApi.getAll });
  const { data: metrics = [] } = useQuery({ queryKey: ['metrics'], queryFn: metricsApi.getAll });
  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: initiatives = [] } = useQuery({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });

  const [showNewMetric, setShowNewMetric] = useState(false);
  const [form, setForm] = useState({ name: '', unit: '', direction: 'higher', cadence: 'Monthly', target: '', baseline: '' });
  const [projectBorn, setProjectBorn] = useState(false);
  const [originInitiativeId, setOriginInitiativeId] = useState('');
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({ name: '', ownerId: '' });
  const [confirmDeleteMetric, setConfirmDeleteMetric] = useState<string | null>(null);

  const service = services.find((s: any) => s.serviceId === id);
  const serviceMetrics = metrics.filter((m: any) => m.serviceId === id);

  const createMetric = useMutation({
    mutationFn: () =>
      metricsApi.create({
        serviceId: id,
        name: form.name,
        unit: form.unit,
        direction: form.direction,
        cadence: form.cadence,
        target: form.target ? Number(form.target) : undefined,
        baseline: form.baseline ? Number(form.baseline) : undefined,
        origin: projectBorn ? 'Project' : 'Pre-existing',
        originInitiativeId: projectBorn ? originInitiativeId : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      setForm({ name: '', unit: '', direction: 'higher', cadence: 'Monthly', target: '', baseline: '' });
      setProjectBorn(false);
      setOriginInitiativeId('');
      setShowNewMetric(false);
      toast.success('Metric added.');
    },
  });

  const updateService = useMutation({
    mutationFn: () => servicesApi.update(id!, headerForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      setEditingHeader(false);
      toast.success('Service updated.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update service'),
  });

  const deleteMetric = useMutation({
    mutationFn: (metricId: string) => metricsApi.delete(metricId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      setConfirmDeleteMetric(null);
      toast.success('Metric removed.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to remove metric'),
  });

  const personName = (pid: string) => people.find((p: any) => p.personId === pid)?.personName || '';

  if (!service) return <p className="np-sub container-xxl flex-grow-1 container-p-y">Loading…</p>;

  const linkedInitiatives = initiatives.filter((i: any) => i.targetMetricIds.some((mid: string) => serviceMetrics.some((m: any) => m.metricId === mid)));

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-service-line"
        title={service.name}
        subtitle={`Owner: ${personName(service.ownerId) || 'Unassigned'}`}
        breadcrumbs={[{ label: 'Service Catalog', to: '/tracker/catalog' }, { label: service.name }]}
        actions={
          editingHeader ? (
            <div className="d-flex gap-2 align-items-center">
              <input className="np-input" style={{ maxWidth: 240 }} value={headerForm.name} onChange={(e) => setHeaderForm({ ...headerForm, name: e.target.value })} />
              <div style={{ minWidth: 200 }}>
                <Select
                  options={people.map((p: any) => ({ value: p.personId, label: p.personName }))}
                  value={(() => {
                    const opt = people.find((p) => p.personId === headerForm.ownerId);
                    return opt ? { value: opt.personId, label: opt.personName } : null;
                  })()}
                  onChange={(opt) => setHeaderForm({ ...headerForm, ownerId: opt?.value ?? '' })}
                  placeholder="— owner —"
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={reactSelectStyles()}
                />
              </div>
              <button className="np-btn np-btn-primary np-btn-sm" disabled={updateService.isPending} onClick={() => updateService.mutate()}>Save</button>
              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setEditingHeader(false)}>Cancel</button>
            </div>
          ) : (
            <>
              <button className="np-icon-btn" onClick={() => { setEditingHeader(true); setHeaderForm({ name: service.name, ownerId: service.ownerId }); }} title="Rename / reassign owner">
                <i className="ri-edit-line"></i>
              </button>
              <button className="np-btn np-btn-primary np-btn-sm" onClick={() => setShowNewMetric((v) => !v)}>
                <i className="ri-add-line"></i>New metric
              </button>
            </>
          )
        }
      />

      {showNewMetric && (
        <div className="np-card">
          <div className="np-card-body">
            <div className="row g-2 mb-2">
              <div className="col-md-6"><input className="np-input" placeholder="Metric name, e.g. Mean-Time-To-Contain" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="col-md-3"><input className="np-input" placeholder="Unit (hrs, %, £…)" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
              <div className="col-md-3">
                <select className="np-select" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                  <option value="higher">Higher is better</option>
                  <option value="lower">Lower is better</option>
                </select>
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <select className="np-select" value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}>
                  {['Weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-md-4"><input className="np-input" placeholder="Baseline value" value={form.baseline} onChange={(e) => setForm({ ...form, baseline: e.target.value })} /></div>
              <div className="col-md-4"><input className="np-input" placeholder="Target value" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></div>
            </div>

            <div className="form-check mb-2">
              <input className="form-check-input" type="checkbox" id="projectBornCheck" checked={projectBorn} onChange={(e) => { setProjectBorn(e.target.checked); if (!e.target.checked) setOriginInitiativeId(''); }} />
              <label className="form-check-label small" htmlFor="projectBornCheck">
                This metric exists because of a project <span className="np-sub">— tags it as project-born and links it back for traceability</span>
              </label>
            </div>
            {projectBorn && (
              <div className="mb-2" style={{ maxWidth: 360 }}>
                <select className="np-select" value={originInitiativeId} onChange={(e) => setOriginInitiativeId(e.target.value)}>
                  <option value="">— which initiative? —</option>
                  {initiatives.map((i: any) => <option key={i.initiativeId} value={i.initiativeId}>{i.name}</option>)}
                </select>
              </div>
            )}

            <div className="d-flex gap-2">
              <button className="np-btn np-btn-primary np-btn-sm" disabled={!form.name.trim() || (projectBorn && !originInitiativeId)} onClick={() => createMetric.mutate()}>Add metric</button>
              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setShowNewMetric(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="np-grid np-grid-3">
        {serviceMetrics.map((m: any) => (
          <div className="np-card" key={m.metricId}>
            <div className="np-card-body">
              <div className="d-flex justify-content-between align-items-start">
                <Link to={`/tracker/catalog/metrics/${m.metricId}`} className="np-row-title fw-semibold">{m.name}</Link>
                <button className="np-icon-btn np-icon-btn-danger" style={{ flexShrink: 0 }} onClick={() => setConfirmDeleteMetric(m.metricId)} title="Delete metric">
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              <p className="np-sub mb-2">Target: {m.target ?? '—'} {m.unit} ({m.direction === 'higher' ? 'higher is better' : 'lower is better'})</p>
              <div className="d-flex gap-1">
                <span className={`np-chip np-tone-${m.origin === 'Project' ? 'primary' : 'neutral'}`}>{m.origin}</span>
                {m.trackedAsBau && <span className="np-chip np-tone-success">BAU tracked</span>}
              </div>
              {confirmDeleteMetric === m.metricId && (
                <div className="d-flex align-items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px solid var(--np-border)' }}>
                  <span className="np-sub" style={{ color: 'var(--bs-danger-text-emphasis)' }}>Delete this metric?</span>
                  <button className="np-btn np-btn-danger np-btn-sm" disabled={deleteMetric.isPending} onClick={() => deleteMetric.mutate(m.metricId)}>Yes</button>
                  <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setConfirmDeleteMetric(null)}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {serviceMetrics.length === 0 && (
          <div className="np-card" style={{ gridColumn: '1 / -1' }}>
            <div className="np-card-body text-center py-4">
              <i className="ri-line-chart-line d-block mb-2" style={{ fontSize: '1.75rem', color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">No metrics on this service yet.</p>
            </div>
          </div>
        )}
      </div>

      {linkedInitiatives.length > 0 && (
        <div className="np-card">
          <div className="np-card-head"><h2>Linked initiatives</h2></div>
          <div className="np-card-body">
            {linkedInitiatives.map((i: any) => (
              <Link key={i.initiativeId} to={`/tracker/initiatives/${i.initiativeId}`} className="d-flex align-items-center gap-2 py-1 np-row-title">
                <i className="ri-flashlight-line"></i>{i.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
